/**
 * State Sync Notifications Hook
 *
 * Wiederverwendbare Notification-Logik für State-Changes
 * Kann im Sidecar UND im External Window verwendet werden
 *
 * Zeigt Toast/Notification wenn sich State über eventBus ändert
 */

import { onMounted, onUnmounted, ref } from 'vue';

import type { StateChangePayload } from '@/configs/eventBus';
import { eventBus } from '@/configs/eventBus';

/**
 * Kontext wo die Notification benutzt wird
 */
export type NotificationContext = 'mainPanel' | 'sidecar' | 'externalWindow';

export interface StateSyncNotificationConfig {
  /**
   * Wo wird dieser Hook verwendet?
   * - 'mainPanel': Linke Spalte (Sender der Changes)
   * - 'sidecar': Rechte Spalte (Receiver)
   * - 'externalWindow': Externes Window
   */
  context: NotificationContext;

  /**
   * Custom Handler wenn sich State ändert
   * Kann zum Updaten von lokalen Werten verwendet werden
   * Kann `showNotification()` aufrufen für custom Messages
   */
  onStateChange?: (payload: StateChangePayload, showNotification: (msg: string) => void) => void;
}

export function useStateSyncNotifications(config: StateSyncNotificationConfig) {
  const notification = ref<{
    visible: boolean;
    message: string;
    timeout?: number;
  }>({
    visible: false,
    message: '',
  });

  /**
   * Zeigt Notification und versteckt sie nach 3 Sekunden
   */
  const showNotification = (message: string) => {
    if (notification.value.timeout) {
      clearTimeout(notification.value.timeout);
    }

    notification.value.visible = true;
    notification.value.message = message;

    notification.value.timeout = window.setTimeout(() => {
      notification.value.visible = false;
    }, 3000);
  };

  /**
   * Hört auf State-Changes und zeigt Notifikation
   */
  const handleStateSync = (payload: StateChangePayload) => {
    // Custom handler if defined (with access to showNotification)
    if (config.onStateChange) {
      config.onStateChange(payload, showNotification);
      // Handler can call showNotification itself for custom messages
      return;
    }

    // Default: show standard notification
    const defaultMsg = `📡 State synced: ${payload.key} = ${JSON.stringify(payload.value)}`;
    showNotification(defaultMsg);
  };

  onMounted(() => {
    eventBus.on('stateSync', handleStateSync);
  });

  onUnmounted(() => {
    eventBus.off('stateSync', handleStateSync);
  });

  return {
    notification,
    showNotification,
  };
}
